import { useMemo } from "react";
import { Info } from "lucide-react";
import { LightTooltip } from "../../ui/tooltip";
import { InputFields } from "./input-fields";
import { LabelFields } from "./label-fields";
import { parseCondition } from "../../data-driven-form/parseCondition";
import { DROPDOWN_WITH_TEXTFIELD, HORIZONTAL_LINE } from "../../../utils/InputFieldConfig";
import { cn } from "../../../lib/utils";

function getColWidthClass(xs) {
  const map = {
    12: "md:w-full",
    9: "md:w-3/4",
    8: "md:w-2/3",
    6: "md:w-1/2",
    4: "md:w-1/3",
    3: "md:w-1/4",
    2: "md:w-1/6",
    1: "md:w-1/12",
  };
  return map[xs] || "md:w-full";
}

export const FormRenderer = (props) => {
  const {
    item,
    onFieldChange,
    onFieldBlur,
    fieldValue,
    componentContext,
    fieldValues,
    isSubField,
    ability,
    readOnly,
    autoFocus,
    auditData,
    customClasses = {},
    handleFieldLevelHandler,
    currentPiiInfoFields,
    supplementAddressData = {},
    highlightField = false,
    stickyContainer = false,
    removeAestrik = false
  } = props;

  const { xs = 3 } = item || {};
  const { condition } = item || {};
  const { visible } = parseCondition(condition, componentContext, fieldValue);

  function getAuditObjectByName(name) {
    const audit = auditData?.audit;
    if (name && audit) {
      if (audit[name]) {
        return audit[name].length > 1 && audit[name];
      }
      const regexp = /^(\w+)\.(\d+)\.(.+)$/;
      const [prefix, index] = name.split(".");
      const keys = Object.keys(audit);
      for (let i = 0; i < keys.length; i++) {
        const match = keys[i].match(regexp);
        if (
          match &&
          match[1] === prefix &&
          Number.parseInt(match[2]) >= index
        ) {
          return audit[keys[i]].length > 1 && audit[keys[i]];
        }
      }
    }
    return null;
  }

  const steps = getAuditObjectByName(item?.pathToValue);

  const error = useMemo(() => {
    const fieldError = componentContext?.errorMessages?.[item?.pathToValue];
    if (!fieldError || fieldError === "") {
      return { isError: false, message: null };
    }
    if (typeof fieldError === "object") {
      return {
        isError: false,
        message: null,
        infoContainer: {
          isInfoMessage: true,
          messageType:
            componentContext?.errorMessages[item?.pathToValue]?.infoType,
          infoMessage:
            componentContext?.errorMessages[item?.pathToValue]?.infoMessage,
        },
      };
    }
    return { isError: true, message: fieldError };
  }, [componentContext, item?.pathToValue]);

  if (!visible || item?.hidden) return <></>;

  if (ability && item?.permission) {
    const { action, subject, negatePermission } = item.permission;
    if (negatePermission) {
      if (ability.can(action, subject)) return <></>;
    } else if (!ability.can(action, subject)) return <></>;
  }

  const isContainer = item?.type?.toLowerCase() === "container";
  const widthClass = getColWidthClass(xs);

  let labelTitle = "";
  if (item?.type?.toLowerCase() === HORIZONTAL_LINE || fieldValue === "--") {
    labelTitle = "";
  } else if (item?.type?.toLowerCase() === DROPDOWN_WITH_TEXTFIELD) {
    labelTitle = fieldValue;
  } else {
    labelTitle = String(
      LabelFields(
        fieldValue,
        item,
        error,
        onFieldChange,
        {}, // classes not used
        readOnly,
        fieldValues,
        componentContext,
        handleFieldLevelHandler,
        currentPiiInfoFields
      ) ?? ""
    );
  }

  return (
    <div
      className={cn(
        "w-full",
        isContainer ? "py-0" : "py-2",
        isSubField ? "px-1" : "px-2", // Reduce horizontal padding for subfields
        widthClass,
        isSubField && "pt-4",
        customClasses?.container
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-2",
          highlightField && "rounded-md ring-2 ring-gray-300 p-2",
          customClasses?.innerContainer
        )}
      >
        {/* Label row */}
        {item?.label ? (
          <div
            className={cn(
              "flex items-center gap-2",
              readOnly ? customClasses?.labelRow : ""
            )}
          >
            <label
              className={cn(
                readOnly ? "block text-xs font-normal text-neutral-7 mb-1" : "block text-sm font-medium mb-1",
                customClasses?.label
              )}
            >
              {item.label}
              {item.optional && (
                <span className="text-neutral-7 ml-1 text-sm font-medium">
                  (Optional)
                </span>
              )}
            </label>

            {readOnly && steps?.length > 1 && (
              <LightTooltip
                title={
                  <div className="text-sm">
                    {steps?.map((s, i) => (
                      <div
                        key={`${s?.actor}-${i}`}
                        className="flex flex-col py-1"
                      >
                        <span className="font-medium">{s?.value}</span>
                        <span className="text-gray-600">
                          {s?.actor} • {s?.timestamp}
                        </span>
                      </div>
                    ))}
                  </div>
                }
                side="right"
              >
                <button
                  className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  aria-label="Show history"
                  type="button"
                >
                  <Info className="h-4 w-4 text-gray-700" />
                </button>
              </LightTooltip>
            )}

            {item?.infoIcon && item?.infoData && (
              <LightTooltip
                title={<div className="text-sm">{item?.infoData}</div>}
                side={
                  item?.tooltipPlacement?.startsWith("left")
                    ? "left"
                    : item?.tooltipPlacement?.startsWith("top")
                    ? "top"
                    : item?.tooltipPlacement?.startsWith("bottom")
                    ? "bottom"
                    : "right"
                }
              >
                <button
                  className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  aria-label="Info"
                  type="button"
                >
                  <Info
                    className="h-4 w-4"
                    style={{ color: item?.infoIconColor || "#000000" }}
                  />
                </button>
              </LightTooltip>
            )}

            {item?.required && !item?.hideLabel && !readOnly && !removeAestrik &&(
              <span className="relative -top-[6px] right-[7px] text-red-500 ml-1">
                *
              </span>
            )}
          </div>
        ) : (
          !item?.hideLabel && <span className="opacity-0">No Label</span>
        )}

        {/* Value row */}
        <div
          className={isContainer ? "flex flex-col" : ""}
          style={
            stickyContainer
              ? { paddingBottom: error?.message ? 0 : "20px" }
              : {}
          }
        >
          {readOnly ? (
            <span className="block">
              <span
                className={cn(
                  "inline-block py-1 w-full",
                  isContainer ? "flex" : "",
                  customClasses?.readLabelValue || "truncate"
                )}
                title={labelTitle}
              >
                {LabelFields(
                  fieldValue,
                  item,
                  error,
                  onFieldChange,
                  {}, // classes not used
                  readOnly,
                  fieldValues,
                  componentContext,
                  handleFieldLevelHandler,
                  currentPiiInfoFields
                )}
              </span>
            </span>
          ) : (
            <InputFields
              fieldValue={fieldValue}
              item={item}
              error={error}
              onFieldChange={onFieldChange}
              fieldValues={fieldValues}
              onFieldBlur={onFieldBlur}
              disabled={item?.disabled}
              autoFocus={autoFocus}
              customClasses={customClasses}
              currentPiiInfoFields={currentPiiInfoFields}
              handleFieldLevelHandler={handleFieldLevelHandler}
              supplementAddressData={supplementAddressData}
            />
          )}
        </div>
      </div>
    </div>
  );
};
